import React, { Component } from 'react';
import { listEvents } from '../utils/eventsApi';
import { emitir } from '../utils/giftCardsApi';
import { errorDeMonto, errorDeCantidad, fechaHora } from '../utils/giftCards';
import { MENU_CATEGORIES } from '../utils/eventIncluded';

var config = process.env;

// Emitir un premio de evento o una cortesía (backend docs/spec-premios-cortesias.md FR-001):
// tarjetas de plata SIN venta, caja ni factura. Pide el motivo y el nombre de quien la da (el
// login del local es compartido). Al confirmar, el padre muestra los códigos para copiarlos a mano
// en las tarjetas, igual que en la venta (GiftCardCodesModal).
//
// Vouchers (backend docs/spec-premios-cortesias-vouchers.md FR-102): además de plata, la tarjeta
// puede valer un producto de cafetería —ítems puntuales, una o más categorías, o cualquier cosa
// hasta un precio, N unidades en el mismo pedido— o mesa para N personas.
//
// Modal controlado por React, sin el JS de Bootstrap (como el resto de la pantalla).
const MODAL = { display: 'block', background: 'rgba(0,0,0,.4)', overflowY: 'auto' };

// Los eventos que tiene sentido premiar: no cancelados, los más recientes primero (la API ya los
// manda por fecha descendente). Treinta alcanzan para el torneo de la semana pasada.
const EVENTOS_A_MOSTRAR = 30;

class GiftCardIssueModal extends Component {

    state = {
        origin: 'premio', eventId: '', amount: '', quantity: '1', reason: '', responsable: '',
        // Qué vale: 'monto' | 'producto' | 'mesa'.
        kind: 'monto',
        label: '', matchKind: 'category', categories: [], items: [], priceCap: '', units: '1', seats: '4',
        menu: [], menuSearch: '',
        eventos: [], eventosError: null, enviando: false, error: null,
    };

    // El menú de cafetería para elegir ítems puntuales (sólo se pide si se elige ese modo).
    cargarMenu = () => {
        if (this.state.menu.length > 0) return;
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'cafeteria/catalog', {
            headers: { 'access-token': localStorage.getItem('token') },
        })
            .then((r) => r.json())
            .then((data) => { if (!(data.error > 0)) this.setState({ menu: data.data || [] }); })
            .catch(() => {});
    }

    toggle = (campo, valor) => this.setState((prev) => {
        const actual = prev[campo];
        return { [campo]: actual.includes(valor) ? actual.filter((v) => v !== valor) : [...actual, valor], error: null };
    });

    async componentDidMount() {
        try {
            const data = await listEvents();
            const eventos = ((data && data.events) || [])
                .filter((e) => e.status !== 'cancelled')
                .slice(0, EVENTOS_A_MOSTRAR);
            this.setState({ eventos });
        } catch (e) {
            this.setState({ eventosError: e.message });
        }
    }

    set = (campo) => (e) => this.setState({ [campo]: e.target.value, error: null });

    validar = () => {
        const { origin, eventId, amount, quantity, reason, responsable, kind, label, matchKind, categories, items, priceCap, units, seats } = this.state;
        if (origin === 'premio' && eventId === '') return 'Elegí el evento del premio.';
        if (kind === 'monto') {
            if (String(amount).trim() === '') return 'Poné el monto de cada tarjeta.';
            const errMonto = errorDeMonto(amount);
            if (errMonto) return errMonto;
        } else if (kind === 'producto') {
            if (label.trim() === '') return 'Escribí qué es (p. ej. "Una bebida").';
            if (matchKind === 'category' && categories.length === 0) return 'Elegí al menos una categoría.';
            if (matchKind === 'products' && items.length === 0) return 'Elegí al menos un ítem del menú.';
            if (matchKind === 'price_cap' && !(Number.isInteger(Number(priceCap)) && Number(priceCap) >= 1)) return 'Poné el precio máximo en pesos enteros.';
            if (!(Number.isInteger(Number(units)) && Number(units) >= 1 && Number(units) <= 20)) return 'Unidades: entre 1 y 20.';
        } else if (!(Number.isInteger(Number(seats)) && Number(seats) >= 1 && Number(seats) <= 20)) {
            return 'Mesa para cuántas personas: entre 1 y 20.';
        }
        const errCantidad = errorDeCantidad(quantity);
        if (errCantidad) return errCantidad;
        if (reason.trim() === '') return origin === 'premio' ? 'Escribí qué premio es (p. ej. "1er puesto").' : 'Escribí el motivo de la cortesía.';
        if (responsable.trim().length < 2) return 'Escribí tu nombre: queda registrado quién la dio.';
        return null;
    }

    confirmar = async () => {
        if (this.state.enviando) return;
        const error = this.validar();
        if (error) {
            this.setState({ error });
            return;
        }
        const { origin, eventId, amount, quantity, reason, responsable, kind, label, matchKind, categories, items, priceCap, units, seats } = this.state;
        this.setState({ enviando: true, error: null });
        const beneficio = kind === 'producto'
            ? {
                label: label.trim(),
                matchKind,
                matchValue: matchKind === 'category' ? categories.join(',') : matchKind === 'products' ? items.join(',') : String(Number(priceCap)),
                units: Number(units),
            }
            : kind === 'mesa' ? { seats: Number(seats), label: label.trim() || undefined } : { amount: Number(amount) };
        try {
            const data = await emitir({
                origin,
                eventId: origin === 'premio' ? Number(eventId) : null,
                kind,
                ...beneficio,
                quantity: Number(quantity),
                reason: reason.trim(),
                responsable: responsable.trim(),
            });
            this.props.onIssued((data && data.giftCards) || []);
        } catch (e) {
            this.setState({ enviando: false, error: e.message });
        }
    }

    // Producto de cafetería: qué es, contra qué se cuenta y cuántas unidades (todas en un pedido).
    renderProducto() {
        const { label, matchKind, categories, items, priceCap, units, menu, menuSearch, enviando } = this.state;
        const filtro = menuSearch.trim().toLowerCase();
        const visibles = menu.filter((m) => filtro === '' || String(m.name || '').toLowerCase().includes(filtro)).slice(0, 40);
        return (
            <React.Fragment>
                <div className="form-group">
                    <label htmlFor="issue-label">Qué es (se ve en el POS)</label>
                    <input id="issue-label" type="text" maxLength={80} className="form-control" value={label}
                        placeholder="Una bebida" disabled={enviando} onChange={this.set('label')} />
                </div>
                <div className="form-group">
                    <label htmlFor="issue-match">Qué cubre</label>
                    <select id="issue-match" className="custom-select" value={matchKind} disabled={enviando}
                        onChange={(e) => { this.setState({ matchKind: e.target.value, error: null }); if (e.target.value === 'products') this.cargarMenu(); }}>
                        <option value="category">Cualquier ítem de una o más categorías</option>
                        <option value="products">Ítems puntuales del menú</option>
                        <option value="price_cap">Cualquier ítem hasta un precio</option>
                    </select>
                </div>
                {matchKind === 'category' && (
                    <div className="form-group">
                        {MENU_CATEGORIES.map((c) => (
                            <div key={c.key} className="custom-control custom-checkbox">
                                <input type="checkbox" className="custom-control-input" id={`issue-cat-${c.key}`} disabled={enviando}
                                    checked={categories.includes(c.key)} onChange={() => this.toggle('categories', c.key)} />
                                <label className="custom-control-label" htmlFor={`issue-cat-${c.key}`}>{c.label}</label>
                            </div>
                        ))}
                    </div>
                )}
                {matchKind === 'products' && (
                    <div className="form-group">
                        <input type="search" className="form-control form-control-sm mb-2" placeholder="Buscar en el menú" aria-label="Buscar en el menú"
                            value={menuSearch} onChange={this.set('menuSearch')} />
                        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e1e5eb', borderRadius: 4, padding: '4px 8px' }}>
                            {menu.length === 0 && <small className="text-muted">Cargando el menú…</small>}
                            {visibles.map((m) => (
                                <div key={m.code} className="custom-control custom-checkbox">
                                    <input type="checkbox" className="custom-control-input" id={`issue-item-${m.code}`} disabled={enviando}
                                        checked={items.includes(String(m.code))} onChange={() => this.toggle('items', String(m.code))} />
                                    <label className="custom-control-label" htmlFor={`issue-item-${m.code}`}>{m.name}</label>
                                </div>
                            ))}
                        </div>
                        {items.length > 0 && <small className="text-muted">{items.length} ítem(s) elegido(s)</small>}
                    </div>
                )}
                {matchKind === 'price_cap' && (
                    <div className="form-group">
                        <label htmlFor="issue-cap">Precio máximo de lista ($)</label>
                        <input id="issue-cap" type="number" min="1" className="form-control" value={priceCap}
                            disabled={enviando} onChange={this.set('priceCap')} />
                    </div>
                )}
                <div className="form-group">
                    <label htmlFor="issue-units">Unidades (todas en el mismo pedido)</label>
                    <input id="issue-units" type="number" min="1" max="20" className="form-control" value={units}
                        disabled={enviando} onChange={this.set('units')} />
                </div>
            </React.Fragment>
        );
    }

    render() {
        const { origin, eventId, amount, quantity, reason, responsable, eventos, eventosError, enviando, error, kind, seats } = this.state;
        return (
            <div id="modal-gift-card-issue" className="modal" role="dialog" style={MODAL}>
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Emitir premio o cortesía</h4>
                        </div>
                        <div className="modal-body">
                            <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                                Son gift cards que nadie compró: no generan venta, caja ni factura. Pueden valer plata, un
                                producto de cafetería o mesa para N personas. Vencen igual que las vendidas.
                            </p>
                            <div className="form-group">
                                <label htmlFor="issue-origin">Qué es</label>
                                <select id="issue-origin" className="custom-select" value={origin} disabled={enviando}
                                    onChange={(e) => this.setState({ origin: e.target.value, error: null })}>
                                    <option value="premio">Premio de un evento</option>
                                    <option value="cortesia">Cortesía</option>
                                </select>
                            </div>
                            {origin === 'premio' && (
                                <div className="form-group">
                                    <label htmlFor="issue-event">Evento</label>
                                    <select id="issue-event" className="custom-select" value={eventId} disabled={enviando}
                                        onChange={this.set('eventId')}>
                                        <option value="">Elegí el evento…</option>
                                        {eventos.map((e) => (
                                            <option key={e.id} value={e.id}>{e.title} · {fechaHora(e.starts_at || e.startsAt)}</option>
                                        ))}
                                    </select>
                                    {eventosError && <small className="text-danger">No se pudieron cargar los eventos: {eventosError}</small>}
                                </div>
                            )}
                            <div className="form-group">
                                <label htmlFor="issue-kind">Qué vale</label>
                                <select id="issue-kind" className="custom-select" value={kind} disabled={enviando}
                                    onChange={(e) => this.setState({ kind: e.target.value, error: null })}>
                                    <option value="monto">Plata (se usa como una gift card)</option>
                                    <option value="producto">Un producto de cafetería</option>
                                    <option value="mesa">Mesa para N personas</option>
                                </select>
                            </div>
                            {kind === 'monto' && (
                                <div className="form-group">
                                    <label htmlFor="issue-amount">Monto de cada tarjeta ($)</label>
                                    <input id="issue-amount" type="number" min="1" className="form-control" value={amount}
                                        disabled={enviando} onChange={this.set('amount')} />
                                </div>
                            )}
                            {kind === 'producto' && this.renderProducto()}
                            {kind === 'mesa' && (
                                <div className="form-group">
                                    <label htmlFor="issue-seats">Mesa para cuántas personas</label>
                                    <input id="issue-seats" type="number" min="1" max="20" className="form-control" value={seats}
                                        disabled={enviando} onChange={this.set('seats')} />
                                    <small className="text-muted">Cubre esos lugares de la mesa sin cargo. Si se sientan menos, la mesa igual queda para ese número.</small>
                                </div>
                            )}
                            <div className="form-group">
                                <label htmlFor="issue-quantity">Cantidad de tarjetas</label>
                                <input id="issue-quantity" type="number" min="1" className="form-control" value={quantity}
                                    disabled={enviando} onChange={this.set('quantity')} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="issue-reason">{origin === 'premio' ? 'Premio' : 'Motivo'}</label>
                                <input id="issue-reason" type="text" maxLength={255} className="form-control" value={reason}
                                    placeholder={origin === 'premio' ? '1er puesto' : 'Cliente habitual, compensación…'}
                                    disabled={enviando} onChange={this.set('reason')} />
                            </div>
                            <div className="form-group mb-0">
                                <label htmlFor="issue-responsable">Tu nombre</label>
                                <input id="issue-responsable" type="text" maxLength={60} className="form-control" value={responsable}
                                    placeholder="Quién la da" disabled={enviando} onChange={this.set('responsable')} />
                            </div>
                            {error && <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" disabled={enviando} onClick={this.props.onClose}>Cancelar</button>
                            <button type="button" className="btn btn-accent" disabled={enviando} onClick={this.confirmar}>
                                {enviando ? 'Emitiendo…' : 'Emitir'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default GiftCardIssueModal;
